import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Pencil, Trash2 } from 'lucide-react'
import '@/features/visualization/_legacy/components/ContextMenu.css'

/** What was right-clicked. A dependency is named by its two ends, the way the drafts store it. */
export type CanvasMenuTarget =
    | { kind: 'object'; objectKey: string }
    | { kind: 'edge'; dependentObject: string; providerObject: string }

export interface CanvasMenuState {
    target: CanvasMenuTarget
    /** Where the pointer was, in viewport coordinates. */
    x: number
    y: number
    open: boolean
}

/**
 * The template canvas's right-click menus: an object's (edit it, delete it) and a dependency's
 * (remove it). A dependency used to offer how its instances pair up; every template object is one
 * object now, so there is nothing to pair (Andrew, 2026-09-22).
 *
 * They are drawn exactly as the planning canvas draws its own menus, from the same stylesheet and
 * markup (ContextMenu.css): the same box, rows, 14px icons and red Delete. The
 * user's words, 2026-09-22: "Use the same menu style on templates". The planning canvas's menus
 * are the right ones and are not to change.
 *
 * The gesture matches too. Right-click opens the menu at the pointer, the browser's own menu does
 * not appear, and a left click opens nothing. The menu closes when an item is chosen, on Escape, on
 * a press anywhere outside it, and when the canvas starts to pan or zoom (the page passes
 * onMoveStart), rather than being left pointing at where something was.
 *
 * Rendered into <body>. `position: fixed` measures from the viewport only while no ancestor has a
 * transform, and the editor sits inside the app's layout, so the menu must not depend on what
 * wraps it.
 */
export function CanvasMenu({
    menu,
    onOpenChange,
    onEditObject,
    onDeleteObject,
    onRemoveEdge,
}: {
    menu: CanvasMenuState | null
    onOpenChange: (open: boolean) => void
    onEditObject: (objectKey: string) => void
    onDeleteObject: (objectKey: string) => void
    onRemoveEdge: (dependentObject: string, providerObject: string) => void
}) {
    const { t } = useTranslation()
    const box = useRef<HTMLDivElement>(null)
    const open = !!menu?.open

    // Escape, or a press outside the menu, closes it. A press on another object starts the next
    // right-click, which then opens that object's menu.
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onOpenChange(false) }
        const onPress = (e: MouseEvent) => {
            if (box.current && !box.current.contains(e.target as Node)) onOpenChange(false)
        }
        document.addEventListener('keydown', onKey)
        document.addEventListener('mousedown', onPress, true)
        return () => {
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('mousedown', onPress, true)
        }
    }, [open, onOpenChange])

    if (!menu || !open) return null
    const target = menu.target
    const choose = (act: () => void) => () => { onOpenChange(false); act() }

    if (target.kind === 'object') {
        return createPortal(
            <div
                ref={box}
                className="context-menu"
                role="menu"
                aria-label={target.objectKey}
                style={{ top: `${menu.y}px`, left: `${menu.x}px` }}
                onContextMenu={(e) => e.preventDefault()}
            >
                <div className="context-menu-option" role="menuitem" onClick={choose(() => onEditObject(target.objectKey))}>
                    <Pencil size={14} />{t('templates.canvasMenu.editObject', 'Edit object')}
                </div>
                {/* Takes its dependencies with it. Nothing is saved until Save, so there is no
                    confirmation, as in the object dialog. */}
                <div className="context-menu-option context-menu-option-danger" role="menuitem" onClick={choose(() => onDeleteObject(target.objectKey))}>
                    <Trash2 size={14} />{t('templates.canvasMenu.deleteObject', 'Delete object')}
                </div>
            </div>,
            document.body,
        )
    }

    return createPortal(
        <div
            ref={box}
            className="context-menu"
            role="menu"
            aria-label={t('templates.canvasMenu.edgeTitle', '{{dependent}} depends on {{provider}}', {
                dependent: target.dependentObject, provider: target.providerObject,
            })}
            style={{ top: `${menu.y}px`, left: `${menu.x}px` }}
            onContextMenu={(e) => e.preventDefault()}
        >
            <div
                className="context-menu-option context-menu-option-danger"
                role="menuitem"
                onClick={choose(() => onRemoveEdge(target.dependentObject, target.providerObject))}
            >
                <Trash2 size={14} />{t('templates.canvasMenu.removeDependency', 'Remove dependency')}
            </div>
        </div>,
        document.body,
    )
}
